<script setup lang="ts">
    import moment from "moment";
    import { Slutenvardsbesok, Sida, SidaLabels, Anhorigbesok } from "./types";

    defineProps<{
        items: Array<Slutenvardsbesok | Anhorigbesok>;
    }>();

    const getSidaLabel = (sida: Sida) => SidaLabels[sida] || "";

    const getOperationInfo = (item: Slutenvardsbesok) => {
        if (item.vardplatsbestallning.typoperation) {
            return item.vardplatsbestallning.typoperation;
        }
        return `${item.vardplatsbestallning.huvudoperationskod.code} ${item.vardplatsbestallning.huvudoperationskod.displayName}`;
    };

    const getInDatum = (indatum: string, intid?: string): string => {
        if (!intid) return indatum;
        return moment(`${indatum} ${intid}`).format("YYYY-MM-DD HH:mm");
    };
</script>

<template>
    <table>
        <thead>
            <tr>
                <th>Vårdplats</th>
                <th>PersonID</th>
                <th>Patientnamn</th>
                <th>Vårdgivare</th>
                <th>Operatör</th>
                <th>Indatum</th>
                <th>Utdatum</th>
                <th rowspan="3">Anteckningar</th>
            </tr>
            <tr>
                <th></th>
                <th>Sida</th>
                <th colspan="5">Typoperation/Opkod</th>
            </tr>
            <tr>
                <th></th>
                <th colspan="6">Info till vårdavdelning</th>
            </tr>
        </thead>
        <tbody>
            <template v-for="item in items">
                <template v-if="'patient' in item">
                    <tr :key="`${item.id}-first-row`" class="first-row">
                        <td>
                            <strong>{{ item.sang?.namn ?? "" }}</strong>
                        </td>
                        <td>{{ item.patient.personId_formatted }}</td>
                        <td>
                            {{
                                item.patient.tilltalsnamn_index > -1
                                    ? `${item.patient.tilltalsnamn} ${item.patient.efternamn}`
                                    : item.patient.namn_reverse
                            }}
                        </td>
                        <td>{{ item.vardplatsbestallning.vardgivare.alias }}</td>
                        <td>{{ item.vardplatsbestallning.operator.alias }}</td>
                        <td class="indatum-intid">{{ getInDatum(item.indatum, item.intid) }}</td>
                        <td>{{ item.utdatum }}</td>
                        <td rowspan="3"></td>
                    </tr>
                    <tr :key="`${item.id}-second-row`">
                        <td></td>
                        <td>{{ getSidaLabel(item.vardplatsbestallning.sida) }}</td>
                        <td colspan="5">{{ getOperationInfo(item) }}</td>
                    </tr>
                    <tr :key="`${item.id}-third-row`">
                        <td></td>
                        <td colspan="6">{{ item.vardplatsbestallning.info_till_vardavdelningen }}</td>
                    </tr>
                    <tr :key="`${item.id}-forth-row`" class="forth-row">
                        <td colspan="8"></td>
                    </tr>
                </template>
                <template v-else>
                    <tr :key="`${item.id}-first-row`" class="first-row">
                        <td>
                            <strong>{{ item.sang?.namn ?? "" }}</strong>
                        </td>
                        <td></td>
                        <td>{{ item.namn }}</td>
                        <td></td>
                        <td></td>
                        <td class="indatum-intid">{{ getInDatum(item.indatum, item.intid) }}</td>
                        <td>{{ item.utdatum }}</td>
                        <td rowspan="3"></td>
                    </tr>
                    <tr :key="`${item.id}-second-row`">
                        <td></td>
                        <td></td>
                        <td colspan="5">
                            Anhörig till {{ item.patient_personId_formatted }}
                            {{ item.patient_namn_reverse }}
                        </td>
                    </tr>
                    <tr :key="`${item.id}-third-row`">
                        <td></td>
                        <td colspan="6"></td>
                    </tr>
                    <tr :key="`${item.id}-forth-row`" class="forth-row">
                        <td colspan="8"></td>
                    </tr>
                </template>
            </template>
        </tbody>
    </table>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/form";

    table {
        width: 100%;
        border-collapse: collapse;
        font-size: $font-size-small;
        line-height: $line-height-small;

        thead {
            background-color: $color-grey-background;
            color: $color-table-header;

            th {
                padding-top: 2px;
                padding-left: 12px;
            }

            tr:first-child {
                th {
                    padding-top: 8px;
                }

                th:last-child {
                    vertical-align: top;
                    width: 30%;
                    border-bottom: 1px solid $color-greyline;
                }
            }

            tr:last-child {
                th {
                    border-bottom: 1px solid $color-greyline;
                }
            }
        }

        tbody {
            tr {
                td {
                    padding-top: 6px;
                    padding-left: 12px;

                    strong {
                        font-size: $font-size-small;
                        line-height: $line-height-small;
                        color: $dark-text-color;
                    }
                }

                td.indatum-intid {
                    white-space: nowrap;
                }
            }

            tr.first-row {
                td {
                    padding-top: 8px;
                    padding-left: 12px;
                }
            }

            tr.forth-row {
                td {
                    border-bottom: 1px solid $color-greyline;
                    height: $form-row-gap;
                }
            }
        }
    }

    @media print {
        table tbody tr {
            page-break-inside: avoid;
            break-inside: avoid;
        }
    }
</style>
